import React, { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { bytesText, logUrl, residentSummary, residentUrl, when } from './deviceData';
import JobShell from './JobShell';
import JsonView from './JsonView';

// One resident script on this device in full: the latest value it stored, and the tail of its own log. The result is read from VES's store, so it is
// there while the device is offline; the log is read from the device itself, so it needs the device online and is only fetched on a click.
export default function ResidentDialog({ account, deviceType, deviceId, script, startOn, request, onClose }) {
  const requestRef = useRef(request);
  requestRef.current = request;
  const [view, setView] = useState(startOn);                   // 'result' or 'log'
  const [result, setResult] = useState({ state: 'loading' });
  const [log, setLog] = useState({ state: 'idle' });
  const askedLog = useRef(false);                          // the log is a round trip to the device: ask once, when it is first looked at
  const [lines, setLines] = useState('');

  const loadResult = useCallback(async () => {
    setResult((r) => ({ ...r, state: 'loading' }));
    try {
      const r = await requestRef.current('GET', residentUrl(account, deviceType, deviceId, script.name));
      setResult({ state: 'ok', item: r.data?.item || {} });
    } catch (ex) {
      // A script that has not run yet has nothing stored: say so, not "failed".
      setResult(ex?.response?.status === 404 ? { state: 'none' } : { state: 'error', error: errorText(ex) });
    }
  }, [account, deviceType, deviceId, script.name]);

  const loadLog = useCallback(async (count) => {
    setLog((l) => ({ ...l, state: 'loading' }));
    try {
      const r = await requestRef.current('GET', logUrl(account, deviceType, deviceId, script.name, count));
      setLog(r.data?.online === false ? { state: 'offline', message: r.data.message } : { state: 'ok', ...r.data });
    } catch (ex) {
      setLog({ state: 'error', error: errorText(ex) });
    }
  }, [account, deviceType, deviceId, script.name]);

  useEffect(() => { loadResult(); }, [loadResult]);
  useEffect(() => {
    if (view === 'log' && !askedLog.current) { askedLog.current = true; loadLog(''); }
  }, [view, loadLog]);
  const refresh = () => (view === 'log' ? loadLog(lines) : loadResult());

  return (
    <JobShell title={script.row} ariaLabel="Resident script" onClose={onClose} onRefresh={refresh}>
      <div role="group" aria-label="Show">
        <button type="button" className={`v2-chipbtn${view === 'result' ? ' on' : ''}`} aria-pressed={view === 'result'} onClick={() => setView('result')}>Result</button>
        <button type="button" className={`v2-chipbtn${view === 'log' ? ' on' : ''}`} aria-pressed={view === 'log'} onClick={() => setView('log')}>Log</button>
      </div>
      <div className="v2-sub">{script.name}</div>

      {view === 'result' && (
        <>
          {result.state === 'loading' && <div><span className="v2-spin" role="status" aria-label="Loading" /></div>}
          {result.state === 'error' && <div className="v2-alert err" role="alert">{result.error}</div>}
          {result.state === 'none' && <div className="v2-empty">This script has not stored a result yet.</div>}
          {result.state === 'ok' && (
            <>
              <div className="v2-sub" data-testid="resident-summary">{residentSummary({ ...script.result, ...result.item })}</div>
              <JsonView value={result.item.results} label="Result" maxHeight={100000} />
            </>
          )}
        </>
      )}

      {view === 'log' && (
        <>
          <div className="v2-bar tight">
            <span className="v2-sub grow">What the script itself wrote, newest last. Read from the device, so it must be online.</span>
            <label className="v2-check">Lines<input className="v2-input" type="number" min="1" max="200" placeholder="20" aria-label="Lines" value={lines} onChange={(e) => setLines(e.target.value)} style={{ width: 70, marginLeft: 6 }} /></label>
          </div>
          {(log.state === 'loading' || log.state === 'idle') && <div><span className="v2-spin" role="status" aria-label="Loading" /></div>}
          {log.state === 'error' && <div className="v2-alert err" role="alert">{log.error}</div>}
          {log.state === 'offline' && <div className="v2-alert info" role="status">{log.message}</div>}
          {log.state === 'ok' && (
            <>
              <div className="v2-sub" data-testid="log-summary">
                {`Showing ${log.returned ?? log.lines.length} of ${log.requested ?? '?'} requested line(s) · file ${bytesText(log.sizeBytes)} · last written ${when(log.lastWriteUtc)}`}
              </div>
              <div className="v2-json"><pre className="v2-json-text" aria-label="Log">{log.lines.join('\n')}</pre></div>
            </>
          )}
        </>
      )}
    </JobShell>
  );
}
